import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Collapse, useTheme, IconButton } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const OfflineNotificationBar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Dispatch custom event when notification visibility changes
  useEffect(() => {
    const isVisible = (!isOnline || showReconnected) && !isDismissed;
    const event = new CustomEvent('offlineNotificationVisibility', { detail: { isVisible } });
    window.dispatchEvent(event);
  }, [isOnline, showReconnected, isDismissed]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      setIsDismissed(false);
      
      // Hide "reconnected" message after 5 seconds
      setTimeout(() => {
        setShowReconnected(false);
      }, 5000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
      setIsDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Show notification when offline or just reconnected
  if (isOnline && !showReconnected) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  return (
    <Collapse in={!isOnline || showReconnected}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          width: '100%',
          minHeight: { xs: 28, sm: 22 },
          display: 'flex',
          alignItems: 'center',
          background: isOnline 
            ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' // Green for reconnected
            : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)', // Red for offline
          color: '#ffffff',
          py: 0.75,
          px: 2,
          boxShadow: isOnline
            ? '0 4px 18px rgba(16, 185, 129, 0.45)'
            : '0 4px 18px rgba(239, 68, 68, 0.45)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            width: '100%',
            position: 'relative',
          }}
        >
          {isOnline ? (
            <WifiIcon sx={{ fontSize: 18, color: '#ffffff' }} />
          ) : (
            <WifiOffIcon sx={{ fontSize: 18, color: '#fef3c7' }} />
          )}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              color: '#ffffff',
              letterSpacing: 0.2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            {isOnline 
              ? t('internet_connection_restored') || 'Internet connection restored. You are back online!'
              : t('no_internet_connection') || 'No internet connection. Some features may not work properly.'}
          </Typography>
          <IconButton
            onClick={() => setIsDismissed(true)}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    </Collapse>
  );
};

export default OfflineNotificationBar;
